/**
 * CommandPalette — Cmd+K searchable navigation and action launcher.
 *
 * Groups: Navigation, Actions, AI
 * Uses cmdk for the command menu UI.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { NAV_SECTIONS } from './Sidebar';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Flatten nav sections into searchable items
const NAV_ITEMS = NAV_SECTIONS.flatMap(section =>
  section.items.map(item => ({
    label: item.label,
    path: item.path,
    section: section.header,
    keywords: `${section.header} ${item.label}`.toLowerCase(),
  }))
);

// Action items
const ACTIONS = [
  { label: 'Upload Data', path: '/data-management', keywords: 'upload import csv gl data' },
  { label: 'Refresh Data', path: null, keywords: 'refresh reload sync', action: 'refresh' },
  { label: 'View Approval Queue', path: '/operations', keywords: 'approve pending queue inbox' },
  { label: 'Show AP Exceptions', path: '/activity-tracker?dept=accounts_payable', keywords: 'ap payable exceptions filter' },
  { label: 'Show Escalated', path: '/activity-tracker?status=ESCALATED', keywords: 'escalated urgent critical' },
];

const RECENT_KEY = 'aw_cmd_recent';

function getRecent(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); }
  catch { return []; }
}

function addRecent(label: string) {
  const recent = getRecent().filter(r => r !== label);
  recent.unshift(label);
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, 5)));
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  // Reset search when opening
  useEffect(() => {
    if (open) setSearch('');
  }, [open]);

  const handleSelect = useCallback((label: string, path: string | null, action?: string) => {
    addRecent(label);
    onOpenChange(false);
    if (action === 'refresh') {
      window.location.reload();
      return;
    }
    if (path) navigate(path);
  }, [navigate, onOpenChange]);

  const handleAiQuery = useCallback(() => {
    addRecent(`Ask AI: ${search}`);
    onOpenChange(false);
    // Navigate to workspace with query param
    navigate(`/cfo-dashboard?ask=${encodeURIComponent(search)}`);
  }, [search, navigate, onOpenChange]);

  if (!open) return null;

  const recent = getRecent();
  const hasResults = NAV_ITEMS.some(i => i.keywords.includes(search.toLowerCase())) ||
                     ACTIONS.some(a => a.keywords.includes(search.toLowerCase()));

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => onOpenChange(false)}
        style={{
          position: 'fixed', inset: 0, zIndex: 9998,
          background: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(4px)',
        }}
      />

      {/* Dialog */}
      <div style={{
        position: 'fixed', top: '20%', left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 560, zIndex: 9999,
      }}>
        <Command
          label="Command Palette"
          style={{
            background: 'var(--bg-surface, var(--bg-surface))',
            border: '1px solid var(--border-hover, rgba(0,0,0,0.08))',
            borderRadius: 'var(--radius-lg, 12px)',
            overflow: 'hidden',
            boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          }}
        >
          {/* Search input */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '12px 16px',
            borderBottom: '1px solid var(--border-subtle, rgba(0,0,0,0.06))',
          }}>
            <span style={{ color: 'var(--text-muted, var(--text-muted))', fontSize: '14px' }}>
              &#x1F50D;
            </span>
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder="Search pages, actions, or ask AI..."
              style={{
                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                color: 'var(--text-primary, var(--text-primary))', fontSize: '14px',
                fontFamily: "var(--font-body, var(--font-sans))",
              }}
            />
            <kbd style={{
              fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
              background: 'var(--bg-elevated, var(--bg-elevated))',
              color: 'var(--text-muted, var(--text-muted))',
              border: '1px solid var(--border-subtle, rgba(0,0,0,0.06))',
            }}>ESC</kbd>
          </div>

          {/* Results */}
          <Command.List style={{
            maxHeight: 360, overflowY: 'auto', padding: '8px',
          }}>
            <Command.Empty style={{
              padding: '24px 16px', textAlign: 'center',
              color: 'var(--text-muted, var(--text-muted))', fontSize: '13px',
            }}>
              No results found
            </Command.Empty>

            {/* Recent */}
            {!search && recent.length > 0 && (
              <Command.Group heading={
                <span style={groupHeadingStyle}>Recent</span>
              }>
                {recent.map(label => {
                  const navItem = NAV_ITEMS.find(i => i.label === label);
                  if (!navItem) return null;
                  return (
                    <Command.Item
                      key={`recent-${label}`}
                      value={label}
                      onSelect={() => handleSelect(navItem.label, navItem.path)}
                      style={itemStyle}
                    >
                      <span style={{ color: 'var(--text-muted)', marginRight: '8px' }}>&#x23F0;</span>
                      {label}
                    </Command.Item>
                  );
                })}
              </Command.Group>
            )}

            {/* Navigation */}
            <Command.Group heading={
              <span style={groupHeadingStyle}>Navigation</span>
            }>
              {NAV_ITEMS.map(item => (
                <Command.Item
                  key={item.path}
                  value={`${item.label} ${item.section}`}
                  onSelect={() => handleSelect(item.label, item.path)}
                  style={itemStyle}
                >
                  <span style={{ color: 'var(--text-muted)', marginRight: '8px' }}>&#x2192;</span>
                  <span>{item.label}</span>
                  <span style={{
                    marginLeft: 'auto', fontSize: '10px',
                    color: 'var(--text-hint, var(--text-hint))',
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                  }}>{item.section}</span>
                </Command.Item>
              ))}
            </Command.Group>

            {/* Actions */}
            <Command.Group heading={
              <span style={groupHeadingStyle}>Actions</span>
            }>
              {ACTIONS.map(action => (
                <Command.Item
                  key={action.label}
                  value={`${action.label} ${action.keywords}`}
                  onSelect={() => handleSelect(action.label, action.path, action.action)}
                  style={itemStyle}
                >
                  <span style={{ color: 'var(--text-muted)', marginRight: '8px' }}>&#x26A1;</span>
                  {action.label}
                </Command.Item>
              ))}
            </Command.Group>

            {/* AI query option */}
            {search.length > 2 && (
              <Command.Group heading={
                <span style={groupHeadingStyle}>AI</span>
              }>
                <Command.Item
                  value={`ask ai ${search}`}
                  onSelect={handleAiQuery}
                  style={{ ...itemStyle, color: 'var(--accent, var(--accent))' }}
                >
                  <span style={{ marginRight: '8px' }}>&#x1F916;</span>
                  Ask AI: &ldquo;{search}&rdquo;
                </Command.Item>
              </Command.Group>
            )}
          </Command.List>

          {/* Footer */}
          <div style={{
            padding: '8px 16px',
            borderTop: '1px solid var(--border-subtle, rgba(0,0,0,0.06))',
            display: 'flex', gap: '16px', justifyContent: 'center',
          }}>
            {[
              { label: 'Navigate', keys: ['\u2191', '\u2193'] },
              { label: 'Select', keys: ['\u21B5'] },
              { label: 'Close', keys: ['esc'] },
            ].map(h => (
              <span key={h.label} style={{ fontSize: '10px', color: 'var(--text-hint, var(--text-hint))' }}>
                {h.keys.map(k => (
                  <kbd key={k} style={{
                    padding: '1px 4px', borderRadius: '3px', marginRight: '2px',
                    background: 'var(--bg-elevated, var(--bg-elevated))',
                    border: '1px solid var(--border-subtle)',
                    fontSize: '10px', color: 'var(--text-muted)',
                  }}>{k}</kbd>
                ))}
                <span style={{ marginLeft: '3px' }}>{h.label}</span>
              </span>
            ))}
          </div>
        </Command>
      </div>
    </>
  );
};

const groupHeadingStyle: React.CSSProperties = {
  fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.8px',
  color: 'var(--text-hint, var(--text-hint))', padding: '6px 8px',
};

const itemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', padding: '8px 12px',
  fontSize: '13px', color: 'var(--text-secondary, var(--text-secondary))',
  borderRadius: 'var(--radius-sm, 6px)', cursor: 'pointer',
  transition: 'background var(--duration-fast, 120ms)',
};

export default CommandPalette;
